import React, { useState, useEffect } from 'react';
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Checkbox,
    useDisclosure,
    FormControl,
    FormLabel,
    Box,
    ModalCloseButton,
    Select, Textarea, FormErrorMessage,
} from '@chakra-ui/react';
import fetchWrapper from '../lib/fetch-wrapper';
import { useProfile } from '../lib/profileContext';
import MultiSelect from '../components/multiSelect';
import { useMsal } from '@azure/msal-react';
import { MdAddBox, MdDelete, MdEdit } from 'react-icons/md';
import { topicsContactsOptions } from '../lib/constants';

const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

type PropertyOption = { value: string; label: string };

type ContactFormData = {
    ContactName: string;
    Properties: PropertyOption[];
    Topics: string;
    Description: string;
    EMail: string;
    PhoneNumber: string;
    Active: boolean;
};

const emptyFormData: ContactFormData = {
    ContactName: '',
    Properties: [],
    Topics: '',
    Description: '',
    EMail: '',
    PhoneNumber: '',
    Active: false,
};

const ContactsPage = () => {
    const { instance, accounts } = useMsal();
    const { profile } = useProfile();
    const [records, setRecords] = useState([]);
    const [propertyOptions, setPropertyOptions] = useState<any[]>([]);
    const [emailTouched, setEmailTouched] = useState(false);
    const [selectedRecord, setSelectedRecord]: any = useState(null);
    const [formData, setFormData] = useState<ContactFormData>(emptyFormData);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        void fetchRecords();
        void fetchProperties();
    }, []);

    const fetchProperties = async () => {
        try {
            const fetchInstance = new fetchWrapper();
            const response: any = await fetchInstance.getProperties();

            const formattedOptions = response.data.records
                .map((record: any) => ({
                    value: record.data.Name,
                    label: record.data.Name
                }))
                .sort((a: any, b: any) => a.label.localeCompare(b.label)); // Alphabetical sort

            setPropertyOptions(formattedOptions);
        } catch (error) {
            toast({ title: 'Error fetching properties', status: 'error', duration: 3000 });
        }
    };

    const fetchRecords = async () => {
        try {
            const fetchInstance = new fetchWrapper();
            const response: any = await fetchInstance.fetchRecords('/api/contacts/records');
            setRecords(response.data.records);
        } catch (error) {
            toast({ title: 'Error fetching contacts', status: 'error', duration: 3000 });
        }
    };

    const deleteRecord = async (rowKey: any) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        try {
            const fetchInstance = new fetchWrapper();
            await fetchInstance.deleteRecord(`/api/contacts/remove/${rowKey}`);
            toast({ title: 'Contact deleted', status: 'success', duration: 3000 });
            void fetchRecords();
        } catch (error) {
            toast({ title: 'Error deleting contact', status: 'error', duration: 3000 });
        }
    };

    const reset = () => {
        setFormData(emptyFormData);
        setEmailTouched(false);
    }

    const handleClose = () => {
        reset();
        onClose()
    };

    const handleEdit = (record: any) => {
        setSelectedRecord(record);
        const propertiesRaw = record.data?.Properties;
        const properties = typeof propertiesRaw === 'string' && propertiesRaw.trim()
            ? propertiesRaw.split(', ').filter(Boolean).map((prop: string) => ({ value: prop, label: prop }))
            : [];
        setFormData({
            ContactName: record.data?.ContactName || '',
            Properties: properties,
            Topics: record.data?.Topics || '',
            Description: record.data?.Description || '',
            EMail: record.data?.EMail || '',
            PhoneNumber: record.data?.PhoneNumber || '',
            Active: Boolean(record.data?.Active),
        });
        onOpen();
    };

    const handleAdd = () => {
        setSelectedRecord(null);
        setFormData(emptyFormData);
        onOpen();
    };

    const handlePropertiesChange = (selectedOptions: any) => {
        setFormData({ ...formData, Properties: selectedOptions });
    };

    const handleTopicChange = (e: any) => {
        setFormData({ ...formData, Topics: e.target.value });
    };
    const handleSubmit = async () => {
        if (!formData.ContactName.trim() || !formData.Topics || !formData.Properties.length || !formData.Description.trim() || !formData.EMail.trim()) {
            toast({ title: 'Validation Error', description: 'All fields are required.', status: 'error', duration: 3000, isClosable: true });
            return;
        }
        if (!isValidEmail(formData.EMail.trim())) {
            toast({ title: 'Invalid Email', description: 'Please enter a valid email address.', status: 'error', duration: 3000, isClosable: true });
            return;
        }
        try {
            const fetchInstance = new fetchWrapper();
            const updatedFormData = {
                ...formData,
                Properties: formData.Properties.map((opt: any) => opt.value).join(', '),
            };
            if (selectedRecord) {
                await fetchInstance.editRecord('/api/contacts/edit', { RowKey: selectedRecord.RowKey, data: updatedFormData });
                toast({ title: 'Contact updated', status: 'success', duration: 3000 });
            } else {
                await fetchInstance.addRecord('/api/contacts/add', updatedFormData);
                toast({ title: 'Contact added', status: 'success', duration: 3000 });
            }
            void fetchRecords();
            onClose();
        } catch (error) {
            toast({ title: 'Error saving contact', status: 'error', duration: 3000 });
        }
    };

    return (
        <Box mx="auto" mt="6" bg="#ffffff" height="100%" m="0" px="30px" pt="30px" overflowY="auto">
            <Button
                variant="brand"
                size="lg"
                height="4rem"
                leftIcon={<MdAddBox size="1.5em" />}
                onClick={handleAdd}
            >
                Add Contact
            </Button>
            <Table variant="simple" mt="4">
                <Thead>
                    <Tr>
                        <Th>Contact Name</Th>
                        <Th>Properties</Th>
                        <Th>Topics</Th>
                        <Th>Description</Th>
                        <Th>Email</Th>
                        <Th>Phone Number</Th>
                        <Th>Active</Th>
                        <Th>Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {records.map((record: any) => {
                        const topicLabel =
                            topicsContactsOptions.find((item) => item.value === record.data.Topics)?.label
                            ?? record.data.Topics
                            ?? '—';
                        return (
                        <Tr key={record.RowKey}>
                            <Td>{record.data.ContactName}</Td>
                            <Td>{record.data.Properties}</Td>
                            <Td>{topicLabel}</Td>
                            <Td>{record.data.Description}</Td>
                            <Td>{record.data.EMail}</Td>
                            <Td>{record.data.PhoneNumber}</Td>
                            <Td>{record.data.Active ? 'Yes' : 'No'}</Td>
                            <Td>
                                <Button bg="transparent" leftIcon={<MdEdit color="black" />} onClick={() => handleEdit(record)} />
                                <Button bg="transparent" leftIcon={<MdDelete color="black" />} onClick={() => deleteRecord(record.RowKey)} />
                            </Td>
                        </Tr>
                        );
                    })}
                </Tbody>
            </Table>
            <Modal isOpen={isOpen} size="2xl" closeOnOverlayClick={false} onClose={handleClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{selectedRecord ? 'Edit Contact' : 'Add Contact'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired>
                            <FormLabel>Contact Name</FormLabel>
                            <Input placeholder='Enter Contact Name' value={formData.ContactName} onChange={(e) => setFormData({ ...formData, ContactName: e.target.value })} />
                        </FormControl>
                        <FormControl id="topic" isRequired>
                            <FormLabel>Topic</FormLabel>
                            <Select placeholder="Select a Topic" value={formData.Topics} onChange={handleTopicChange}>
                                {topicsContactsOptions.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl id="properties" isRequired>
                            <FormLabel>Properties</FormLabel>
                            <MultiSelect options={propertyOptions} value={formData.Properties} onChange={handlePropertiesChange} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Description</FormLabel>
                            <Textarea placeholder='Enter Description' value={formData.Description} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} />
                        </FormControl>
                        <FormControl
                            isRequired
                            isInvalid={emailTouched && formData.EMail !== '' && !isValidEmail(formData.EMail)}
                        >
                            <FormLabel>Email</FormLabel>
                            <Input
                                placeholder="Enter Email"
                                value={formData.EMail}
                                onChange={(e) => {
                                    setFormData({ ...formData, EMail: e.target.value });
                                    if (!emailTouched) setEmailTouched(true);
                                }}
                                onBlur={() => setEmailTouched(true)}
                            />
                            <FormErrorMessage>Invalid email format</FormErrorMessage>
                        </FormControl>
                        <FormControl >
                            <FormLabel>Phone Number</FormLabel>
                            <Input placeholder='Enter Phone Number' value={formData.PhoneNumber} onChange={(e) => setFormData({ ...formData, PhoneNumber: e.target.value })} />
                        </FormControl>
                        <Checkbox isChecked={formData.Active} onChange={(e) => setFormData({ ...formData, Active: e.target.checked })}>Active</Checkbox>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="cancel" onClick={handleClose}>Cancel</Button>
                        <Button variant="brand" ml={3} onClick={handleSubmit}>{selectedRecord ? 'Update' : 'Add'}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default ContactsPage;
